import classnames from 'classnames';
import sum from 'lodash/sum';
import sumBy from 'lodash/sumBy';
import { func, number, objectOf, shape, string } from 'prop-types';
import React, { useEffect } from 'react';

import { getSpentBudgetForProduct } from '../../campaign-editor/utils';
import CurrencyFormat from '../../generic/CurrencyFormat';
import { campaignShape } from '../../prop-types';
import { calculateTotalDelta } from '../transfer-budget/utils';
import { WRITABLE_BUDGET_FIELDS } from './constants';
import { hasDeltaChanged } from './utils';

const getTotalSpentBudget = (campaign, productAggregates) =>
  sum(
    productAggregates.map(({ product }) =>
      getSpentBudgetForProduct(campaign, product),
    ),
  );

export default function ChangeProductBudgetTotals({
  productAggregatesMap,
  totalNewBudget,
  totalOldBudget,
  campaign,
  onChange,
}) {
  const productAggregates = Object.values(productAggregatesMap);
  const totalSpentBudget = getTotalSpentBudget(campaign, productAggregates);
  const totalLeftBudget = totalNewBudget - totalSpentBudget;
  const totalDelta = totalNewBudget - totalOldBudget;

  useEffect(() => {
    const roundedDelta = Math.round(totalDelta * 100) / 100;
    onChange({ target: { name: 'delta', value: roundedDelta } });
  }, [onChange, totalDelta]);

  return (
    <tr className="bg-gray-200">
      <td>Totals</td>
      <td>&nbsp;</td>
      {WRITABLE_BUDGET_FIELDS.map(field => {
        const delta = calculateTotalDelta(productAggregates, field);
        const isTotalDelta0 = delta === 0;
        const deltaChange = hasDeltaChanged(productAggregates, field);

        return [
          <td key={`old-${field}`} className="text-right">
            <CurrencyFormat
              name={`total-old-${field}`}
              value={sumBy(productAggregates, `oldValues.${field}`)}
              className="text-nowrap"
              text
            />
          </td>,
          <td key={`new-${field}`} className="text-right">
            <CurrencyFormat
              name={`total-new-${field}`}
              value={sumBy(productAggregates, `newValues.${field}`)}
              className="text-nowrap"
              text
            />
          </td>,
          <td
            key={`delta-${field}`}
            className={classnames('text-right', {
              'cr-bg-red': delta !== 0 && !isTotalDelta0,
              'cr-bg-green': deltaChange && isTotalDelta0,
            })}
          >
            <CurrencyFormat
              name={`total-delta-${field}`}
              value={delta}
              className={classnames('text-nowrap text-info', {
                'cr-text-white': delta !== 0 && !isTotalDelta0,
              })}
              text
            />
          </td>,
        ];
      })}
      <td className="text-right">
        <CurrencyFormat
          name="total-old-budget"
          value={totalOldBudget}
          className="text-nowrap"
          text
        />
      </td>
      <td className="text-right">
        <CurrencyFormat
          name="total-new-budget"
          value={totalNewBudget}
          className="text-nowrap"
          text
        />
      </td>
      <td className="text-right">
        <CurrencyFormat
          name="total-spent-budget"
          value={totalSpentBudget}
          className={classnames('text-nowrap text-danger', {
            'bg-danger text-white': totalNewBudget < totalSpentBudget,
          })}
          text
        />
      </td>
      <td className="text-right">
        <CurrencyFormat
          name="total-left-budget"
          value={totalLeftBudget}
          className="text-nowrap text-success"
          text
        />
      </td>
      <td className="text-right">
        <CurrencyFormat
          name="total-delta"
          value={totalDelta}
          className="text-nowrap text-info"
          text
        />
      </td>
    </tr>
  );
}

const budgetFieldsShape = shape({
  netClientBudgetExternal: number.isRequired,
  wkzBudget: number.isRequired,
  coInvest: number.isRequired,
  freeService: number.isRequired,
});

ChangeProductBudgetTotals.propTypes = {
  productAggregatesMap: objectOf(
    shape({
      label: string.isRequired,
      adjustedFee: number.isRequired,
      oldValues: budgetFieldsShape.isRequired,
      newValues: budgetFieldsShape.isRequired,
    }).isRequired,
  ).isRequired,
  totalNewBudget: number.isRequired,
  totalOldBudget: number.isRequired,
  campaign: campaignShape.isRequired,
  onChange: func.isRequired,
};
